import { useEffect, useState } from "react";
import { FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr } from "@/lib/api";

const SUGGEST = {
  candidate: ["Full resume", "Licenses & certifications", "Police check", "References", "ID verification"],
  business: ["Business registration", "Business license", "Employment contract", "Job description", "Insurance certificate"],
};

export const DocRequestDialog = ({ open, onOpenChange, targetType, targetId, targetName, onCreated, children }) => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTitle(""); setMessage(""); } }, [open]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/doc-requests", { target_type: targetType, target_id: targetId, title, message });
      toast.success("Request sent");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="doc-request-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Request files{targetName ? ` from ${targetName}` : ""}</DialogTitle>
          <DialogDescription>They'll get an email and can upload privately. Only you and our team can open what they share.</DialogDescription>
        </DialogHeader>
        {children}
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>What do you need?</Label><Input data-testid="doc-request-title-input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="flex flex-wrap gap-1.5">
            {(SUGGEST[targetType] || []).map((s) => <button key={s} type="button" onClick={() => setTitle(s)} className="rounded-full border border-stone-300 px-2.5 py-0.5 text-xs text-stone-600 hover:border-slate-900 hover:text-slate-900">{s}</button>)}
          </div>
          <div className="space-y-1.5"><Label>Message</Label><Textarea data-testid="doc-request-message-input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} /></div>
        </div>
        <DialogFooter><Button data-testid="doc-request-submit-button" disabled={busy || !title.trim() || !targetId} onClick={send}>Send request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const NewDocRequestButton = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("candidate");
  const [target, setTarget] = useState("");
  const [lists, setLists] = useState({ candidate: [], business: [] });
  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/candidates"), api.get("/admin/members")]).then(([c, m]) => setLists({
      candidate: c.data.map((x) => ({ id: x.id, name: x.name })),
      business: m.data.map((x) => ({ id: x.id, name: x.company || x.name })),
    }));
  }, [open]);
  return (
    <>
      <Button data-testid="new-doc-request-button" onClick={() => setOpen(true)}><FilePlus2 className="mr-2 h-4 w-4" />New file request</Button>
      <DialogWrap open={open} setOpen={setOpen} type={type} target={target} onCreated={onCreated}>
        <div className="grid grid-cols-2 gap-3">
          <Select value={type} onValueChange={(v) => { setType(v); setTarget(""); }}>
            <SelectTrigger data-testid="doc-request-type-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="candidate">Candidate</SelectItem><SelectItem value="business">Business</SelectItem></SelectContent>
          </Select>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="doc-request-target-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>{lists[type].map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </DialogWrap>
    </>
  );
};

const DialogWrap = ({ open, setOpen, type, target, onCreated, children }) => (
  <DocRequestDialog open={open} onOpenChange={setOpen} targetType={type} targetId={target} onCreated={onCreated}>{children}</DocRequestDialog>
);
